// The Help pane's tour, as a pure state machine (System context PR B). The
// runner (help/useTourRunner) drives it: it navigates when a step is on
// another page, runs the step's action through the page's tools (Help pane
// live tours), outlines the step's control, asks the help route to explain
// the step, and waits for the person's Next. Next, Back and End tour are the
// person's buttons, never the model's, so a tour always moves the same way.
//
// Back never undoes. It lands on the previous step, which is outlined and
// explained again, and its action does not run again: reordering is not
// idempotent and nothing else is worth reversing (Adam, 14 Oct: "Back
// re-shows and re-explains, never undoes"). A step's `did` therefore belongs
// to the step it was done on and never carries across a move.

import type { HelpTour } from '../types/gotypes'
import { featureAudience, sectionForRoute } from './features'
import type { FeatureSection, HelpReader } from './features'
import { canRead } from './features'

export interface TourStep {
  /** A NAV_PAGES key; empty: stay where you are. */
  page?: string
  /** "<section>/<feature>" in the registry. */
  feature?: string
  /** The on-screen label of the control to outline. */
  control?: string
  /** What the author wants said or shown. */
  say?: string
  /** Text the person may send to the page's agent with a button. Never sent for them. */
  type?: string
  /**
   * The exact route name the step needs (e.g. 'editspec'). Set, it replaces
   * the section match: Tables and Edit Spec are one section, so without it a
   * step meant for Edit Spec would "arrive" while still on Tables.
   */
  route?: string
  /** A page tool the runner runs on entering the step (never on Back). */
  action?: TourAction
  /**
   * The on-screen label of a control the runner clicks on entering the step
   * (never on Back), for pages that register no page tool for it
   * (help/walkthroughActions). A label that commits is refused (help/walkthroughGuard).
   */
  click?: string
  /** Text the runner types into the control with this label on entering the step. It never presses Enter. */
  fill?: { control: string; text: string }
}

/** A page tool by name, its arguments (a value may be `{ $pick: name }`, help/tourPicks), and the card's "Next: …" line. */
export interface TourAction {
  tool: string
  args?: Record<string, unknown>
  label: string
}

export interface TourRun {
  /** A customer tour's id, or `section:<key>` for an "Around <section>" tour. */
  id: string
  name: string
  steps: TourStep[]
}

/**
 * - navigating: the step is on another page; waiting for it to mount.
 * - explaining: the step is on screen (outlined and explained); waiting for Next.
 * - asking: the person asked a question mid-step; the step stays.
 * - done: no tour.
 */
export type TourPhase = 'navigating' | 'explaining' | 'asking' | 'done'

export interface TourState {
  tour: TourRun | null
  index: number
  phase: TourPhase
  /** What this step's action did on screen, as the page tool reported it. */
  did?: string
  /** Pick results kept for the run, e.g. first_folder → its path (help/tourPicks). */
  picked: Record<string, string>
}

export const NO_TOUR: TourState = { tour: null, index: 0, phase: 'done', picked: {} }

/** `here` is the route name on screen when the event happened. */
export type TourEvent =
  /** `index`: land on this step instead of the first (a walkthrough resumed after a reload). */
  | { type: 'start'; tour: TourRun; here: string; index?: number }
  | { type: 'next'; here: string }
  | { type: 'back'; here: string }
  | { type: 'arrived' }
  | { type: 'ask' }
  | { type: 'answered' }
  | { type: 'close' }
  /** The step's action ran; `did` is what it reported. */
  | { type: 'acted'; did: string }
  /** A pick resolved during the run (help/tourPicks). */
  | { type: 'picked'; name: string; value: string }

/**
 * Whether a step's page is already on screen: the same route, or a route in
 * the same section (a tour step on 'agent' is met by an open session).
 */
export function onPage(page: string | undefined, here: string): boolean {
  if (!page || page === here) return true
  const want = sectionForRoute(page)
  return !!want && want === sectionForRoute(here)
}

/** Whether a step is on screen: its exact route when it names one, else its page's section. */
export function stepOnScreen(step: TourStep | undefined, here: string): boolean {
  if (step?.route) return step.route === here
  return onPage(step?.page, here)
}

function land(tour: TourRun, index: number, here: string, picked: Record<string, string>): TourState {
  return { tour, index, phase: stepOnScreen(tour.steps[index], here) ? 'explaining' : 'navigating', picked }
}

export function tourReducer(state: TourState, event: TourEvent): TourState {
  switch (event.type) {
    case 'start':
      return event.tour.steps.length
        ? land(event.tour, Math.min(Math.max(event.index ?? 0, 0), event.tour.steps.length - 1), event.here, {})
        : NO_TOUR
    case 'close':
      return NO_TOUR
  }
  const { tour } = state
  if (!tour) return state
  switch (event.type) {
    case 'next':
      return state.index + 1 >= tour.steps.length ? NO_TOUR : land(tour, state.index + 1, event.here, state.picked)
    case 'back':
      return state.index === 0 ? state : land(tour, state.index - 1, event.here, state.picked)
    case 'acted':
      return { ...state, did: event.did }
    case 'picked':
      return { ...state, picked: { ...state.picked, [event.name]: event.value } }
    case 'arrived':
      return state.phase === 'navigating' ? { ...state, phase: 'explaining' } : state
    case 'ask':
      return state.phase === 'explaining' ? { ...state, phase: 'asking' } : state
    case 'answered':
      return state.phase === 'asking' ? { ...state, phase: 'explaining' } : state
  }
  return state
}

/** The step on screen, or undefined with no tour. */
export function currentStep(state: TourState): TourStep | undefined {
  return state.tour?.steps[state.index]
}

/** A customer's authored tour (System context → Tours). Older tours (page, type, say) run unchanged. */
export function tourFromHelpTour(t: HelpTour): TourRun {
  return {
    id: t.id,
    name: t.name,
    steps: t.steps.map((s) => ({
      page: s.page || undefined,
      feature: s.feature || undefined,
      control: s.control || undefined,
      say: s.say || undefined,
      type: s.type || undefined,
      ...(s.action?.tool ? { action: { tool: s.action.tool, args: s.action.args, label: s.action.label } } : {}),
    })),
  }
}

/**
 * "Around <section>": a step for each feature this reader may see, on the
 * section's main page, outlining the feature's first control.
 */
export function tourFromSection(section: FeatureSection, reader: HelpReader): TourRun {
  return {
    id: `section:${section.key}`,
    name: `Around ${section.title}`,
    steps: section.features
      .filter((f) => canRead(reader, featureAudience(section, f)))
      .map((f) => ({
        page: section.navPage,
        feature: `${section.key}/${f.key}`,
        control: f.controls?.[0],
      })),
  }
}
